//#region IMPORTS
import {
  instoreTypesSelectors,
  clearInstoreTypeParams,
  deleteInstoreTypeAsync,
} from "../../features/instoreTypes/instoreTypesSlice";
import {
  DELETE_INSTORE_TYPE_SURE,
  CREATE_OR_UPDATE_INSTORE_TYPES_MODAL,
} from "../../models/_consts";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Fragment, useState } from "react";
import { InstoreType } from "../../models/instoreType";
import { useTranslation } from "react-i18next";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { toast } from "react-toastify";
import InstoreTypesFilterBar from "./InstoreTypesFilterBar";
import InstoreTypesTable from "./InstoreTypesTable";
import InstoreTypeForm from "./InstoreTypeForm";
import MainContainer from "../_layout/MainContainer";
import GoBackButton from "../genericButtons/goBackButton/GoBackButton";
import AreYouSure from "../modalTypes/AreYouSure";
import PageHeader from "../genericPageHeader/PageHeader";
import MainModal from "../_layout/MainModal";
import useAdminManager from "../../hooks/useAdminManager";
import useHQmanager from "../../hooks/useHQmanager";
//#endregion

//#region INTERFACE
interface Props {
  title?: string;
}
//#endregion

export default function InstoreTypesContainer({
  title = "rituals.instoreTypes",
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Redux
  const dispatch = useAppDispatch();
  const instoreTypes = useAppSelector(instoreTypesSelectors.selectAll);
  const { loading, instoreTypes_Loaded, metaData, status } = useAppSelector(
    ({ instoreTypes }) => instoreTypes
  );
  const { user } = useAppSelector(({ account }) => account);

  // Local state
  const [savingDataUpload] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<InstoreType>();

  // Roles
  const isAdmin = useAdminManager(user);
  const hqTeam = useHQmanager(user);
  //#endregion

  //#region LOGIC
  // Create or update type
  const createOrUpdateTypeHandler = (instoreType?: InstoreType) => {
    setSelectedType(instoreType);
    dispatch(openModal(CREATE_OR_UPDATE_INSTORE_TYPES_MODAL));
  };

  // Delete type warning
  const deleteTypeWarningHandler = (instoreType: InstoreType) => {
    setSelectedType(instoreType);
    dispatch(openModal(DELETE_INSTORE_TYPE_SURE));
  };

  // Delete type confirm
  const deleteTypeHandler = async () => {
    if (selectedType) {
      await dispatch(deleteInstoreTypeAsync({ id: selectedType.id }));
      dispatch(openModal(false));
    }
  };

  // Cancel create or update promo
  const cancelCRUDType = () => {
    if (!savingDataUpload) {
      setSelectedType(undefined);
      dispatch(openModal(false));
    } else {
      toast.error("Some background tasks are stil running. Please wait...");
    }
  };

  // Reset handler
  const resetHandler = () => {
    setSelectedType(undefined);
    dispatch(clearInstoreTypeParams());
  };
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      {/* MODALS */}
      <MainModal type={DELETE_INSTORE_TYPE_SURE} closeHandler={cancelCRUDType}>
        <AreYouSure
          confirmHandler={deleteTypeHandler}
          closeHandler={cancelCRUDType}
          message={`Are you sure you want to delete type ${selectedType?.name}?`}
          title="Delete type?"
        />
      </MainModal>

      <MainModal
        type={CREATE_OR_UPDATE_INSTORE_TYPES_MODAL}
        closeHandler={cancelCRUDType}
      >
        <InstoreTypeForm
          instoreType={selectedType}
          cancelCUD={cancelCRUDType}
          disabled={loading && status.includes("PendingCreateInstoreType")}
        />
      </MainModal>

      {/* BODY */}
      <MainContainer>
        <PageHeader
          createClickHandler={() => createOrUpdateTypeHandler(undefined)}
          createButtonValue={t("rituals.addType")}
          disabled={loading}
          isAdmin={isAdmin}
          hqTeam={hqTeam}
          hqCanAdd
          title={t(title)}
          reset={resetHandler}
        >
          <GoBackButton />
        </PageHeader>

        <InstoreTypesFilterBar />

        <InstoreTypesTable
          editHandler={(item: InstoreType) => createOrUpdateTypeHandler(item)}
          deleteHandler={(item: InstoreType) => deleteTypeWarningHandler(item)}
          itemsLoaded={instoreTypes_Loaded}
          metaData={metaData}
          loading={loading}
          items={instoreTypes}
          hide={instoreTypes_Loaded && instoreTypes.length <= 0}
        />
      </MainContainer>
    </Fragment>
  );
  //#endregion
}
